import { useContext, useMemo } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useBackupBridge } from '@/src/renderer/lib/backup/backupBridge'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { useOpenPreferences } from '@/src/renderer/hooks/useOpenPreferences'
import { isAwaitingFirstSync, SyncContext } from '@/src/renderer/contexts/SyncProvider'
import type { QuickNotesEmptyStateProps } from './QuickNotesEmptyState'

/**
 * Die zwei Wege für eine **leere App** (#158, Entscheidung 15): „Ich habe ein Backup …“ (nur am
 * Desktop) und „Aus anderer App importieren …“. Hat HappyTask Inhalt, gibt es sie nicht — mit dem
 * ersten Eintrag verschwinden sie.
 *
 * Mit Geräte-Sync erst nach dem ersten Sync (Entscheidung 2a): Ein neues Gerät ist bis dahin nur scheinbar
 * leer, und „Kurz notiert“ ist der Start-Tab.
 */
export function useEmptyAppActions(): Pick<QuickNotesEmptyStateProps, 'onRestoreBackup' | 'onImportFromOtherApp'> {
  const empty = useDataSource().useAppIsEmpty()
  const awaitingSync = isAwaitingFirstSync(useContext(SyncContext))
  const bridge = useBackupBridge()
  const openPreferences = useOpenPreferences()
  return useMemo(() => {
    if (!empty || awaitingSync) return {}
    return {
      ...(getPlatformAPI().platform === 'electron'
        ? { onRestoreBackup: () => void bridge.openAndRestore().catch(error => console.error('[Backup] Öffnen fehlgeschlagen:', error)) }
        : {}),
      // Auf die Übersicht „Import & Export“: Spätere Quellen tauchen dort ohne Änderung hier auf.
      onImportFromOtherApp: () => openPreferences({ topNavId: 'account-data', subNavId: 'import' }),
    }
  }, [empty, awaitingSync, bridge, openPreferences])
}
